import { useEffect, useState } from "react";
import { AxiosProgressEvent } from "axios";
import { CircularProgress } from "@mui/material";
import { Icon } from "@vds/icons";
import { Body, FlexBox, IconButton, Select } from "components";
import { ProgressBar } from "components/forms/DragAndDrop/DragAndDrop.module";
import {
  FileRowWrapper,
  FileCard,
  ThumbnailWrapper,
  ThumbnailImage,
  FileNameWrapper,
  FileNameText,
  RemoveButtonWrapper,
  SelectColumn,
} from "./BrandComplianceForm.module";

export interface BrandComplianceFileRowProps {
  file: File;
  progressEvent: AxiosProgressEvent;
  uploading: boolean;
  deleting: boolean;
  onDelete?: (file: File) => void;
  assetType: string;
  assetTypes: { id: string; name: string }[];
  onAssetTypeChange: (value: string) => void;
  error?: boolean;
  errorText?: string;
}

const BrandComplianceFileRow = ({
  file,
  progressEvent,
  uploading,
  deleting,
  onDelete,
  assetType,
  assetTypes,
  onAssetTypeChange,
  error,
  errorText,
}: BrandComplianceFileRowProps) => {
  const isImage = file.type.startsWith("image/");
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  // Memoize the object URL per file and revoke it on cleanup / when the file
  // changes, so we don't leak blob URLs as rows are added/removed.
  useEffect(() => {
    if (!isImage) {
      setThumbnailUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setThumbnailUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file, isImage]);

  const loaded =
    progressEvent?.loaded < file.size ? progressEvent.loaded : file.size;
  const isUploadInProgress = !!progressEvent && progressEvent.progress !== 1;

  const handleDelete = () => {
    onDelete && onDelete(file);
  };

  const handleAssetTypeChange = (e: any) => {
    onAssetTypeChange(e?.target?.value ?? "");
  };

  return (
    <FileRowWrapper>
      <FileCard>
        <ThumbnailWrapper>
          {isImage && thumbnailUrl ? (
            <ThumbnailImage src={thumbnailUrl} alt={file.name} />
          ) : (
            <Icon name="single-document" size="medium" />
          )}
        </ThumbnailWrapper>

        <FileNameWrapper>
          <FileNameText>
            <Body bold testId={`file-name-${file.name}`}>
              {file.name}
            </Body>
          </FileNameText>
          {isUploadInProgress ? (
            <FlexBox gap="0.25rem">
              <ProgressBar percentage={loaded / file.size} />
              <Body size="small">
                {`${(loaded / 1024 ** 2).toFixed(2)}MB / ${(
                  file.size /
                  1024 ** 2
                ).toFixed(2)}MB (${Math.ceil((loaded / file.size) * 100)}% complete)`}
              </Body>
            </FlexBox>
          ) : (
            <Body size="small">{`${(file.size / 1024 ** 2).toFixed(2)}MB`}</Body>
          )}
        </FileNameWrapper>

        <RemoveButtonWrapper>
          {uploading ? (
            <CircularProgress size={20} />
          ) : (
            !!onDelete && (
              <IconButton
                name="close"
                size="small"
                ariaLabel="remove file"
                onClick={handleDelete}
                disabled={deleting}
                testId={`remove-file-${file.name}`}
              />
            )
          )}
        </RemoveButtonWrapper>
      </FileCard>

      <SelectColumn data-testid={`asset-type-${file.name}`}>
        <Select
          label="Asset Type"
          value={assetType}
          items={assetTypes}
          optionLabels={["name"]}
          valueKey="id"
          placeholderLabel="Select"
          onChange={handleAssetTypeChange}
          error={!!error}
          errorText={errorText}
        />
      </SelectColumn>
    </FileRowWrapper>
  );
};

export default BrandComplianceFileRow;
